import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription 
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Crown, User, Edit, Trash2, Shield, ShieldOff, Search,
  Mail, UserCircle, Check, X, UserPlus, Image, Plus, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsers = ({ users = [], onRefresh }) => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // 创建用户状态
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    name: '',
    password: '',
    user_type: 'artist',
    bio: ''
  });

  // 作品管理状态
  const [showArtworksDialog, setShowArtworksDialog] = useState(false);
  const [selectedUserForArtworks, setSelectedUserForArtworks] = useState(null);
  const [userArtworks, setUserArtworks] = useState([]);
  const [artworksLoading, setArtworksLoading] = useState(false);
  const [showAddArtworkDialog, setShowAddArtworkDialog] = useState(false);
  const [newArtwork, setNewArtwork] = useState({
    title: '',
    image_url: '',
    description: '',
    year: '',
    medium: '',
    dimensions: ''
  });
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  
  // 上传头像
  const handleUploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('头像图片不能超过5MB');
      return;
    }
    
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, { headers });
      setEditForm({ ...editForm, avatar: res.data.url });
      toast.success('头像上传成功');
    } catch (err) {
      toast.error('头像上传失败');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };
  
  // 用户类型选项
  const userTypeOptions = [
    { value: 'artist', label: '画家' },
    { value: 'collector', label: '收藏家' },
    { value: 'gallery', label: '美术馆' },
    { value: 'enthusiast', label: '艺术爱好者' },
  ];

  // 过滤用户
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 管理员列表
  const admins = filteredUsers.filter(u => u.role === 'admin');
  const regularUsers = filteredUsers.filter(u => u.role !== 'admin');

  // 打开编辑对话框
  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditForm({
      email: user.email || '',
      name: user.name || '',
      avatar: user.avatar || '',
      bio: user.bio || user.creator_bio || '',
      user_title: user.user_title || '',
      is_creator: user.is_creator || false,
      admin_languages: user.admin_languages || [],
    });
  };

  // 保存用户修改
  const handleSaveUser = async () => {
    if (!editingUser) return;
    setLoading(true);
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, editForm, { headers });
      toast.success('用户信息已更新');
      setEditingUser(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  // 设为管理员
  const handleSetAdmin = async (user) => {
    if (!confirm(`确定要将 ${user.email} 设为管理员吗？`)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/${user.id}/set-admin`, {}, { headers });
      toast.success(`已将 ${user.email} 设为管理员`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  // 移除管理员权限
  const handleRemoveAdmin = async (user) => {
    if (!confirm(`确定要移除 ${user.email} 的管理员权限吗？`)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/${user.id}/remove-admin`, {}, { headers });
      toast.success(`已移除 ${user.email} 的管理员权限`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除用户
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, { headers });
      toast.success(`已删除用户 ${userToDelete.email}`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  // 创建新用户
  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.name || !createForm.password) {
      toast.error('请填写邮箱、名称和密码');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/create`, createForm, { headers });
      toast.success(`已成功创建用户 ${createForm.email}`);
      setShowCreateDialog(false);
      setCreateForm({
        email: '',
        name: '',
        password: '',
        user_type: 'artist',
        bio: ''
      });
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  // ===== 作品管理方法 =====
  
  // 打开作品管理对话框
  const openArtworksDialog = async (user) => {
    setSelectedUserForArtworks(user);
    setShowArtworksDialog(true);
    await fetchUserArtworks(user.id);
  };

  // 获取用户作品
  const fetchUserArtworks = async (userId) => {
    setArtworksLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/artworks`, { headers });
      setUserArtworks(res.data);
    } catch (err) {
      toast.error('获取作品失败');
      setUserArtworks([]);
    } finally {
      setArtworksLoading(false);
    }
  };

  // 上传作品图片
  const handleArtworkImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingArtwork(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setNewArtwork(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('图片上传成功');
    } catch (err) {
      toast.error('图片上传失败');
    } finally {
      setUploadingArtwork(false);
    }
  };

  // 创建作品
  const handleCreateArtwork = async () => {
    if (!newArtwork.title || !newArtwork.image_url) {
      toast.error('请填写作品标题并上传图片');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUserForArtworks.id}/artworks`,
        newArtwork,
        { headers }
      );
      toast.success('作品添加成功');
      setShowAddArtworkDialog(false);
      setNewArtwork({ title: '', image_url: '', description: '', year: '', medium: '', dimensions: '' });
      await fetchUserArtworks(selectedUserForArtworks.id);
      onRefresh?.(); // 刷新用户列表以更新作品数量
    } catch (err) {
      toast.error(err.response?.data?.detail || '添加失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除作品
  const handleDeleteArtwork = async (artworkId) => {
    if (!confirm('确定要删除这件作品吗？')) return;
    
    try {
      await axios.delete(
        `${API}/admin/users/${selectedUserForArtworks.id}/artworks/${artworkId}`,
        { headers }
      );
      toast.success('作品已删除');
      await fetchUserArtworks(selectedUserForArtworks.id);
      onRefresh?.();
    } catch (err) {
      toast.error('删除失败');
    }
  };

  // 用户卡片组件
  const UserCard = ({ user, isAdmin: isAdminUser }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-lg border hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAdminUser ? 'bg-yellow-100' : 'bg-amber-100'}`}>
          {isAdminUser ? (
            <Crown className="w-5 h-5 text-yellow-600" />
          ) : (
            <User className="w-5 h-5 text-amber-700" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{user.name || '未命名'}</span>
            {isAdminUser && <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">管理员</Badge>}
            {user.is_creator && <Badge variant="outline" className="text-purple-600 border-purple-300">创作者</Badge>}
            {/* 显示语言权限标签 */}
            {isAdminUser && user.admin_languages && user.admin_languages.length > 0 && (
              <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                <Globe className="w-3 h-3 mr-1" />
                {user.admin_languages.map(l => 
                  l === 'zh' ? '中' : 
                  l === 'en' ? 'EN' : 
                  l === 'ru' ? 'RU' : 
                  l === 'uk' ? 'UA' : 
                  l === 'fr' ? 'FR' : l
                ).join('/')}
              </Badge>
            )}
          </div>
          <div className="text-sm text-amber-600 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {user.email}
          </div>
          {user.user_title && (
            <div className="text-xs text-purple-500 mt-1">{user.user_title}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* 作品管理按钮 */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => openArtworksDialog(user)} 
          title="管理作品"
          className="text-purple-600 hover:text-purple-700"
        >
          <Image className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} title="编辑">
          <Edit className="w-4 h-4" />
        </Button>
        {isAdminUser ? (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleRemoveAdmin(user)}
            title="移除管理员"
            className="text-orange-600 hover:text-orange-700"
          >
            <ShieldOff className="w-4 h-4" />
          </Button>
        ) : (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleSetAdmin(user)}
            title="设为管理员"
            className="text-green-600 hover:text-green-700"
          >
            <Shield className="w-4 h-4" />
          </Button>
        )}
        {!isAdminUser && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setUserToDelete(user); setShowDeleteDialog(true); }}
            title="删除用户"
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">用户管理</h2>
        <div className="flex items-center gap-4">
          <div className="text-sm text-amber-600">
            共 {users.length} 个用户，其中 {admins.length} 个管理员
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            创建用户
          </Button>
        </div>
      </div>

      {/* 搜索框 */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-amber-500" />
        <Input
          placeholder="搜索用户邮箱或名称..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* 管理员列表 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-600" />
            管理员 ({admins.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {admins.length === 0 ? (
            <div className="text-center text-amber-600 py-4">没有管理员</div>
          ) : (
            admins.map(user => <UserCard key={user.id} user={user} isAdmin={true} />)
          )}
        </CardContent>
      </Card>

      {/* 普通用户列表 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-amber-700" />
            普通用户 ({regularUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-96 overflow-y-auto">
          {regularUsers.length === 0 ? (
            <div className="text-center text-amber-600 py-4">没有普通用户</div>
          ) : (
            regularUsers.map(user => <UserCard key={user.id} user={user} isAdmin={false} />)
          )}
        </CardContent>
      </Card>

      {/* 编辑用户对话框 */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>编辑用户</DialogTitle>
            <DialogDescription>修改用户的基本信息</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 头像上传 */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {editForm.avatar ? (
                  <img 
                    src={getImageUrl(editForm.avatar)} 
                    alt="头像" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center border-2 border-dashed border-amber-300">
                    <UserCircle className="w-10 h-10 text-amber-500" />
                  </div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium">用户头像</label>
                <div className="flex gap-2 mt-1">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadAvatar}
                      className="hidden"
                      disabled={uploadingAvatar}
                    />
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Image className="w-4 h-4 mr-1" />
                        {uploadingAvatar ? '上传中...' : '上传头像'}
                      </span>
                    </Button>
                  </label>
                  {editForm.avatar && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditForm({ ...editForm, avatar: '' })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-amber-600 mt-1">支持JPG、PNG，最大5MB</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">邮箱</label>
              <Input
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="用户邮箱"
              />
            </div>
            <div>
              <label className="text-sm font-medium">名称</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="用户名称"
              />
            </div>
            <div>
              <label className="text-sm font-medium">身份标签</label>
              <Input
                value={editForm.user_title}
                onChange={(e) => setEditForm({ ...editForm, user_title: e.target.value })}
                placeholder="如：收藏家 · 旅行家"
              />
            </div>
            <div>
              <label className="text-sm font-medium">个人简介</label>
              <Textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="艺术家简介..."
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_creator"
                checked={editForm.is_creator}
                onChange={(e) => setEditForm({ ...editForm, is_creator: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="is_creator" className="text-sm">设为创作者</label>
            </div>
            
            {/* 语言管理权限 - 仅对管理员显示 */}
            {editingUser?.role === 'admin' && (
              <div className="border-t pt-4 mt-4">
                <label className="text-sm font-medium text-purple-700 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  语言管理权限
                </label>
                <p className="text-xs text-amber-600 mb-3">选择该管理员可以管理哪些语言版本的内容（不勾选则可管理所有语言）</p>
                <div className="flex flex-wrap gap-4">
                  {[
                    { code: 'zh', name: '中文', flag: '🇨🇳' },
                    { code: 'en', name: 'English', flag: '🇬🇧' },
                    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
                    { code: 'uk', name: 'Українська', flag: '🇺🇦' },
                    { code: 'fr', name: 'Français', flag: '🇫🇷' },
                  ].map(lang => (
                    <label key={lang.code} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.admin_languages?.includes(lang.code)}
                        onChange={(e) => {
                          const newLangs = e.target.checked
                            ? [...(editForm.admin_languages || []), lang.code]
                            : (editForm.admin_languages || []).filter(l => l !== lang.code);
                          setEditForm({ ...editForm, admin_languages: newLangs });
                        }}
                        className="rounded"
                      />
                      <span>{lang.flag} {lang.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>取消</Button>
            <Button onClick={handleSaveUser} disabled={loading}>
              {loading ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
            <DialogDescription>
              确定要删除用户 <strong>{userToDelete?.email}</strong> 吗？此操作不可撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>取消</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={loading}>
              {loading ? '删除中...' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 创建用户对话框 */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              创建新用户
            </DialogTitle>
            <DialogDescription>
              为画家、收藏家、美术馆等创建账号。用户可通过邮箱和密码登录接管账户。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">邮箱 <span className="text-red-500">*</span></label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@example.com"
                data-testid="create-user-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium">名称 <span className="text-red-500">*</span></label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="用户显示名称"
                data-testid="create-user-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">密码 <span className="text-red-500">*</span></label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="初始登录密码"
                data-testid="create-user-password"
              />
            </div>
            <div>
              <label className="text-sm font-medium">用户类型</label>
              <Select
                value={createForm.user_type}
                onValueChange={(value) => setCreateForm({ ...createForm, user_type: value })}
              >
                <SelectTrigger data-testid="create-user-type">
                  <SelectValue placeholder="选择用户类型" />
                </SelectTrigger>
                <SelectContent>
                  {userTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">简介</label>
              <Textarea
                value={createForm.bio}
                onChange={(e) => setCreateForm({ ...createForm, bio: e.target.value })}
                placeholder="用户简介（可选）"
                rows={3}
                data-testid="create-user-bio"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>取消</Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-user-submit"
            >
              {loading ? '创建中...' : '创建用户'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 作品管理对话框 */}
      <Dialog open={showArtworksDialog} onOpenChange={setShowArtworksDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-purple-600" />
              {selectedUserForArtworks?.name} 的作品管理
            </DialogTitle>
            <DialogDescription>
              管理该用户的作品，作品将展示在其个人主页和用户列表中
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* 添加作品按钮 */}
            <Button 
              onClick={() => setShowAddArtworkDialog(true)}
              className="mb-4 bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加作品
            </Button>
            
            {/* 作品列表 */}
            {artworksLoading ? (
              <div className="text-center py-8 text-amber-600">加载中...</div>
            ) : userArtworks.length === 0 ? (
              <div className="text-center py-8 text-amber-600">
                <Image className="w-12 h-12 mx-auto mb-2 text-amber-400" />
                暂无作品
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {userArtworks.map(artwork => (
                  <div key={artwork.id} className="relative group rounded-lg overflow-hidden border">
                    <img
                      src={getImageUrl(artwork.image_url)}
                      alt={artwork.title}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                      <p className="text-white text-sm font-medium truncate">{artwork.title}</p>
                      {artwork.year && <p className="text-white/70 text-xs">{artwork.year}</p>}
                      <Button
                        size="sm"
                        variant="destructive"
                        className="mt-2"
                        onClick={() => handleDeleteArtwork(artwork.id)}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        删除
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 添加作品对话框 */}
      <Dialog open={showAddArtworkDialog} onOpenChange={setShowAddArtworkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              添加新作品
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">作品标题 <span className="text-red-500">*</span></label>
              <Input
                value={newArtwork.title}
                onChange={(e) => setNewArtwork({ ...newArtwork, title: e.target.value })}
                placeholder="输入作品标题"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">作品图片 <span className="text-red-500">*</span></label>
              {newArtwork.image_url ? (
                <div className="relative mt-2">
                  <img
                    src={getImageUrl(newArtwork.image_url)}
                    alt="预览"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => setNewArtwork({ ...newArtwork, image_url: '' })}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-amber-50 mt-2">
                  <div className="text-center">
                    <Image className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                    <p className="text-sm text-amber-600">
                      {uploadingArtwork ? '上传中...' : '点击上传图片'}
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleArtworkImageUpload}
                    disabled={uploadingArtwork}
                  />
                </label>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium">作品描述</label>
              <Textarea
                value={newArtwork.description}
                onChange={(e) => setNewArtwork({ ...newArtwork, description: e.target.value })}
                placeholder="描述这件作品"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">创作年份</label>
                <Input
                  value={newArtwork.year}
                  onChange={(e) => setNewArtwork({ ...newArtwork, year: e.target.value })}
                  placeholder="如：2024"
                />
              </div>
              <div>
                <label className="text-sm font-medium">创作媒介</label>
                <Input
                  value={newArtwork.medium}
                  onChange={(e) => setNewArtwork({ ...newArtwork, medium: e.target.value })}
                  placeholder="如：油画"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">尺寸</label>
              <Input
                value={newArtwork.dimensions}
                onChange={(e) => setNewArtwork({ ...newArtwork, dimensions: e.target.value })}
                placeholder="如：100x80cm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddArtworkDialog(false)}>取消</Button>
            <Button 
              onClick={handleCreateArtwork} 
              disabled={loading || !newArtwork.title || !newArtwork.image_url}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? '添加中...' : '添加作品'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
